import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Pencil, Send, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { klarysUrl } from './klarysApi';

export const VAR_RE = /\{([^{}\n]{1,40})\}/g;
export const variablesDe = (t) => [...new Set([...(t || '').matchAll(VAR_RE)].map((m) => m[1].trim()).filter(Boolean))];
const estDate = (v) => /date|échéance|echeance|\bjour\b/i.test(v);
const frDate = (iso) => (iso ? iso.split('-').reverse().join('/') : '');

export const useModeles = (headers) => {
  const [items, setItems] = useState([]);
  const charger = useCallback(() => axios.get(klarysUrl('/modeles'), { headers }).then((r) => setItems(r.data.items)).catch(() => {}), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { charger(); }, [charger]);
  const enregistrer = async (m) => {
    try {
      if (m.id) await axios.patch(klarysUrl(`/modeles/${m.id}`), { texte: m.texte, titre: m.titre }, { headers });
      else await axios.post(klarysUrl('/modeles'), { texte: m.texte, titre: m.titre }, { headers });
      toast.success(m.id ? 'Modèle modifié' : 'Modèle enregistré'); charger(); return true;
    } catch (e) { toast.error(e.response?.data?.detail || 'Enregistrement impossible'); return false; }
  };
  const supprimer = async (m) => {
    try { await axios.delete(klarysUrl(`/modeles/${m.id}`), { headers }); charger(); } catch { toast.error('Suppression impossible'); }
  };
  return { items, charger, enregistrer, supprimer };
};

export const KlarysModeleDialog = ({ initial, onClose, onSave }) => {
  const [texte, setTexte] = useState(initial.texte || '');
  const [titre, setTitre] = useState(initial.titre || '');
  const vars = variablesDe(texte);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="z-[70] max-w-lg" onEscapeKeyDown={(e) => e.stopPropagation()} data-testid="klarys-modele-dialog">
        <DialogHeader>
          <DialogTitle>{initial.id ? 'Modifier le modèle' : 'Enregistrer comme modèle'}</DialogTitle>
          <DialogDescription>Remplacez les parties variables par des cases entre accolades, par exemple {'{nom du membre}'} ou {'{date}'}.</DialogDescription>
        </DialogHeader>
        <input value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Titre court (facultatif)" maxLength={60} className="w-full rounded-md border px-3 py-2 text-sm" data-testid="klarys-modele-titre" />
        <textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={5} className="w-full rounded-md border px-3 py-2 text-sm" data-testid="klarys-modele-texte" />
        <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid="klarys-modele-variables">
          {vars.length ? vars.map((v) => <span key={v} className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-900">{v}{estDate(v) ? ' (date)' : ''}</span>)
            : <span className="text-amber-700">Aucune case à remplir détectée : ajoutez par exemple {'{nom du membre}'}.</span>}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="klarys-modele-annuler">Annuler</Button>
          <Button disabled={!vars.length} onClick={async () => { if (await onSave({ ...initial, texte, titre })) onClose(); }} className="bg-[#0a3d62] hover:bg-[#0c4a77]" data-testid="klarys-modele-enregistrer">Enregistrer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const KlarysModeleForm = ({ modele, onClose, onEnvoyer }) => {
  const vars = variablesDe(modele.texte);
  const [val, setVal] = useState({});
  const rempli = vars.every((v) => (val[v] || '').trim());
  const texte = modele.texte.replace(VAR_RE, (m, v) => { const x = val[v.trim()]; return x ? (estDate(v) ? frDate(x) : x) : m; });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="z-[70] max-w-lg" onEscapeKeyDown={(e) => e.stopPropagation()} data-testid="klarys-modele-form">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-[#0a3d62]" /> {modele.titre}</DialogTitle>
          <DialogDescription>Complétez les cases puis envoyez la demande à Klarys.</DialogDescription>
        </DialogHeader>
        <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); if (rempli) { onEnvoyer(texte); onClose(); } }}>
          {vars.map((v, i) => (
            <label key={v} className="block text-sm font-medium text-slate-700">{v}
              <input autoFocus={i === 0} type={estDate(v) ? 'date' : 'text'} value={val[v] || ''} onChange={(e) => setVal({ ...val, [v]: e.target.value })}
                className="mt-0.5 w-full rounded-md border px-3 py-2 text-sm font-normal" data-testid={`klarys-modele-champ-${i}`} />
            </label>
          ))}
          <p className="rounded-md bg-slate-50 p-2 text-xs text-slate-700 whitespace-pre-wrap" data-testid="klarys-modele-apercu">{texte}</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={!rempli} className="bg-[#0a3d62] hover:bg-[#0c4a77]" data-testid="klarys-modele-envoyer"><Send className="mr-1 h-4 w-4" /> Envoyer</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const KlarysModelesBar = ({ mod, disabled, onRemplir, onEditer, onPartager }) => {
  if (!mod.items.length) return null;
  return (
    <div className="mb-2 flex flex-wrap items-center gap-1.5" data-testid="klarys-modeles-bar">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Modèles</span>
      {mod.items.map((m, i) => (
        <span key={m.id} className="group inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 pl-2.5 pr-1 py-0.5 text-xs text-amber-900">
          <button type="button" disabled={disabled} onClick={() => onRemplir(m)} title={m.texte} data-testid={`klarys-modele-${i}`} className="max-w-[180px] truncate">
            <FileText className="mr-1 inline h-3 w-3" />{m.titre}
          </button>
          <button type="button" onClick={() => onEditer(m)} title="Modifier" data-testid={`klarys-modele-${i}-editer`} className="rounded-full p-0.5 hover:bg-amber-100"><Pencil className="h-3 w-3" /></button>
          <button type="button" onClick={() => onPartager(m)} title="Partager" data-testid={`klarys-modele-${i}-partager`} className="rounded-full p-0.5 hover:bg-amber-100"><Send className="h-3 w-3" /></button>
          <button type="button" onClick={() => window.confirm(`Supprimer le modèle « ${m.titre} » ?`) && mod.supprimer(m)} title="Supprimer" data-testid={`klarys-modele-${i}-supprimer`} className="rounded-full p-0.5 hover:bg-amber-100"><Trash2 className="h-3 w-3" /></button>
        </span>
      ))}
    </div>
  );
};
